<template id="drawer">
  <v-container fluid>
    <v-row v-if="organization">
      <v-col cols="12">
        <v-btn color="primary" block @click="printPreview">Print Preview</v-btn>
      </v-col>
      <v-col cols="12" v-for="selection in fields" :key="selection.key">
        <div v-if="selection.type === 'header'" class="title">{{selection.label}}</div>
        <v-switch
            class="mt-0"
            v-if="selection.type === 'switch'"
            v-model="values[selection.key]"
            :label="selection.label"
            :disabled="selection.disabled"
        ></v-switch>
        <v-text-field
            v-if="selection.type === 'textfield'"
            v-model="values[selection.key]"
            :value="selection.value"
            :label="selection.label"
            hide-details
            outlined
            class="mb-0 pb-0"
            dense
        ></v-text-field>
        <v-select
            v-if="selection.type === 'font_select'"
            :label="selection.label"
            :items="selection.options"
            v-model="values[selection.key]"
            outlined
            hide-details
            :cache-items="selection.cacheItems"
            :item-text="selection.itemText"
            :item-value="selection.itemValue"
        >
          <template v-slot:item="item">
            <span :style="{fontFamily: item.item.value}">{{item.item.text}}</span>
          </template>
          <template v-slot:selection="item">
            <span :style="{fontFamily: item.item.value}">{{item.item.text}}</span>
          </template>
        </v-select>
        <v-select
            v-if="selection.type === 'select'"
            :label="selection.label"
            :items="selection.options"
            v-model="values[selection.key]"
            :outlined="selection.outlined"
            :hide-details="selection.hideDetails"
            :persistent-hint="selection.persistentHint"
            :hint="selection.hint"
            :multiple="selection.multiple"
            :small-chips="selection.smallChips"
            :no-data-text="selection.noDataText"
            :item-text="selection.itemText"
            :item-value="selection.itemValue"
        >
          <template v-if="selection.key === 'product'" v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title>{{ data.item.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ data.item.price.amount }} {{ data.item.price.currency_code }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-select>
        <v-slider
            v-if="selection.type === 'slider'"
            :label="selection.label"
            v-model="values[selection.key]"
            :thumb-label="selection.thumbLabel"
            :min="selection.min"
            :max="selection.max"
            :step="selection.step"
            :persistent-hint="selection.persistentHint"
            :hint="selection.hint"
            :rules="selection.rules"
        ></v-slider>
        <v-expansion-panels v-if="selection.type === 'color_select'">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="subtitle-1">
                {{selection.label}}
                <div class="body-2"
                     :style="{border: '1px solid black', backgroundColor: values[selection.key].hex, display: 'inline-block',
                       height: '15px', width: '40px', marginLeft: '10px', verticalAlign: 'middle'}">
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-color-picker outlined flat v-model="tempValues[selection.key]"></v-color-picker>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col cols="12">
        <v-select
          v-model="selectedTemplate"
          label="Template"
          outlined
          :items="templates"
          item-text="name"
          return-object
        />
        <v-autocomplete
          v-model="selectedFont"
          label="Font"
          outlined
          :items="fonts"
          key="family"
          item-text="family"
          return-object
        />
        <v-switch
          class="mt-0"
          label="Use only numbers on labels"
          v-model="useOnlyNumbers"
        ></v-switch>
        <v-file-input accept="image/*" outlined label="Logo" @change="logoChanged"></v-file-input>
        <v-autocomplete
          v-model="selectedLocations"
          label="Locations"
          outlined
          :items="locations"
          item-text="label"
          item-value="uuid"
          multiple
        />
        <v-autocomplete
          return-object
          label="eTags"
          outlined
          :items="tags"
          multiple
          item-text="readableLabel"
          :menu-props="{ maxWidth: 500 }"
          @change="selectTags"
        >
          <template v-slot:prepend-item>
            <v-list-item
              ripple
              @mousedown.prevent
              @click="selectAllTags"
            >
              <v-list-item-action>
                <v-icon :color="selectedTags.length > 0 ? 'indigo darken-4' : ''">
                  {{ selectAllIcon }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  Select All
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-on="on" v-bind="attrs">
              <v-list-item-action>
                <v-icon>
                  {{ selectedTags.some(t => t && t.unitID === item.unitID) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.readableLabel }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="item.errorText" class="red--text">
                  {{ item.errorText }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="item.errorText">
                  Tag Location: {{ item.location }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12">
        <v-btn color="primary" :disabled="selectedTags.length === 0" block @click="printEtags">Print eTags</v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

export default {
  template: '#drawer',
  name: 'Drawer',
  data() {
    return {
      tempValues: {
        text_color: null,
        background_color: null
      },
      selectedLocations: []
    }
  },
  methods: {
    printEtags() {
      document.getElementById('etags').contentWindow.print()
    },
    selectAllTags() {
      if(this.selectedTags.length === this.tags.length) {
        this.selectTags([])
      } else {
        this.selectTags(this.tags)
      }
    },
    selectTags(tags) {
      this.$store.commit('updateSelectedTags', tags)
    },
    printPreview() {
      document.getElementById('preview').contentWindow.print()
    },
    logoChanged(eventFile) {

      const toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })

      toBase64(eventFile).then(result => {
        this.$store.commit('updateLogo', result)
      })

    }
  },
  created() {

    if(this.$route.query.values) {
      try {
        let queryValues = JSON.parse(this.$route.query.values)
        this.tempValues = queryValues
        this.$store.commit('changeValues', queryValues)
      }
      catch(e) {
        // No handling
        console.log(e)
      }
    }

    this.tempValues = this.$store.getters.values
  },
  computed: {
    fonts() {
      return this.$store.state.fonts
    },
    selectedFont: {
      get() {
        return this.$store.state.selectedFont
      },
      set(value) {
        this.$store.commit('setSelectedFont', value)
      }
    },
    selectedTemplate: {
      get() {
        return this.$store.state.selectedTemplate
      },
      set(value) {
        this.$store.commit('selectTemplate', value)
      }
    },
    useOnlyNumbers: {
      get() {
        return this.$store.state.useOnlyNumbers
      },
      set(value) {
        this.$store.commit('changeUseOnlyNumbers', value)
      }
    },
    templates() {
      return this.$store.state.templates
    },
    selectedTags() {
      return this.$store.state.selectedTags
    },
    selectAllIcon () {
      if (this.selectedTags.length === this.tags.length) {
        return 'mdi-close-box'
      }
      if (this.selectedTags.length > 0) {
        return 'mdi-minus-box'
      }
      return 'mdi-checkbox-blank-outline'
    },
    locations: function() {
      return this.$store.state.locations
    },
    tags() {
      if(this.selectedLocations.length > 0) {
        return this.$store.getters.tags.filter(tag => {
          return this.selectedLocations.includes(tag.location)
        })
      }
      return this.$store.getters.tags
    },
    values: function() {
      return this.$store.state.values
    },
    fields: function() {
      return this.$store.state.fields
    },
    organization: function() {
      return this.$store.state.organization
    }
  }
}
</script>

<style scoped>

</style>
